import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { pickLang, type PairingCharacter, type PairingStateResponse } from "@shared";
import { currentLang } from "../i18n";
import { playFindSound, playMissSound, vibrateFind } from "./findFeedback";
import * as api from "./gameApi";
import MissionTimer from "./MissionTimer";
import { ordreSeme } from "./shuffle";

/**
 * « Les jumeaux » est la seule dragouille au pluriel : la question générique
 * (« Où {{name}} aime-t-elle aller ? ») lui donnerait un accord et un pronom
 * faux, en français comme en anglais (« does the twins »). Détecté par le nom
 * FR — seule langue obligatoire — plutôt que par un identifiant : celui d'un
 * `PairingItem` est un UUID généré à chaque mission, sans lien stable avec
 * `perso-01` de `assets/characters/names.json`.
 */
function estPluriel(nomFr: string): boolean {
  return nomFr.trim().toLowerCase().startsWith("les ");
}

interface Props {
  /** Titre de la mission, tel que l'admin l'a écrit. */
  title: string;
  /** Identifiant de la partie : sert de graine à l'ordre d'affichage. */
  missionId: string;
  startedAt: string;
  busy: boolean;
  onFinish: () => void;
}

/**
 * Mission 2 : associer chaque dragouille à sa planète préférée.
 *
 * Le geste tient en deux touchers — une dragouille, puis un astre. Pas de
 * glisser-déposer : sur un téléphone tenu par un jeune, le glissement rate
 * souvent et se confond avec le défilement de la page.
 *
 * Ici, contrairement à la mission 1, l'app DIT si c'est juste : le jeu
 * consiste précisément à deviner, donc le retour est légitime.
 */
export default function Mission2Screen({ title, missionId, startedAt, busy, onFinish }: Props) {
  const { t } = useTranslation();
  const lang = currentLang();

  const [state, setState] = useState<PairingStateResponse | null>(null);
  const [chosen, setChosen] = useState<PairingCharacter | null>(null);
  /** Astre qui vient d'être refusé : sert à le faire trembler brièvement. */
  const [missed, setMissed] = useState<string | null>(null);
  const [justSolved, setJustSolved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setState(await api.fetchPairing());
    } catch {
      setError("game.error.offline");
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Ordre propre à CETTE partie, pour les dragouilles comme pour les astres.
  // Recalculé à chaque association trouvée — `state` change alors — mais la
  // graine est la même, donc les deux rangées ne bougent pas d'un pixel.
  const personnages = useMemo(
    () => (state === null ? [] : ordreSeme(state.characters, missionId, (c) => c.id)),
    [state, missionId],
  );
  // Graine distincte pour les astres : sans cela, deux listes de même taille
  // se retrouveraient dans le même ordre relatif, et une dragouille finirait
  // systématiquement en face de sa planète.
  const astres = useMemo(
    () => (state === null ? [] : ordreSeme(state.planets, `${missionId}:astres`, (p) => p.id)),
    [state, missionId],
  );

  async function tryPlanet(planetId: string): Promise<void> {
    if (chosen === null) return;
    const character = chosen;

    try {
      const result = await api.attemptPairing(character.id, planetId);

      if (result.correct) {
        playFindSound();
        vibrateFind();
        setChosen(null);
        setJustSolved(character.id);
        window.setTimeout(() => {
          setJustSolved((id) => (id === character.id ? null : id));
        }, 900);
        await reload();
      } else {
        // On garde la dragouille sélectionnée : le jeune réessaie sans avoir
        // à la retoucher, ce qui rend l'erreur beaucoup moins pénible.
        playMissSound();
        setMissed(planetId);
        window.setTimeout(() => {
          setMissed((id) => (id === planetId ? null : id));
        }, 500);
      }
    } catch {
      setError("game.error.offline");
    }
  }

  if (state === null) {
    return (
      <main className="screen screen--centered">
        <p className="card__text card__text--muted">{t("common.loading")}</p>
      </main>
    );
  }

  const percent = state.total === 0 ? 0 : Math.round((state.solved / state.total) * 100);
  const allSolved = state.total > 0 && state.solved === state.total;

  return (
    <main className="screen screen--game screen--pairing">
      <div className="hud">
        <div className="hud__row">
          <span className="hud__count">
            {state.solved} / {state.total}
          </span>
          <h1 className="hud__title">{title}</h1>
          <MissionTimer startedAt={startedAt} />
        </div>
        <div
          className="progress"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="progress__bar" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {error !== null && <p className="form__error">{t(error)}</p>}

      <p className="pairing__instruction">
        {chosen === null
          ? t("pairing.pickCharacter")
          : estPluriel(chosen.nameI18n.fr)
            ? t("pairing.pickPlanetPlural")
            : t("pairing.pickPlanet", { name: pickLang(chosen.nameI18n, lang) })}
      </p>

      {/* Deux colonnes qui défilent CHACUNE de leur côté, verticalement :
          exactement le principe des deux rangées horizontales d'avant,
          tourné de 90°. Ça suppose que l'écran lui-même ne défile plus — voir
          `.screen--pairing`, qui le fixe à la hauteur de l'appareil pour que
          ce soit les colonnes qui défilent, pas la page. */}
      <div className="pairing__columns">
        <ul className="pairing__column">
          {personnages.map((character) => (
            <li key={character.id}>
              <button
                type="button"
                className={`pairing__item ${character.solved ? "pairing__item--solved" : ""} ${
                  chosen?.id === character.id ? "pairing__item--chosen" : ""
                } ${justSolved === character.id ? "pairing__item--pop" : ""}`}
                disabled={character.solved}
                aria-pressed={chosen?.id === character.id}
                onClick={() => {
                  setChosen(chosen?.id === character.id ? null : character);
                }}
              >
                <span className="pairing__image">
                  {character.imageUrl !== null && (
                    <img src={character.imageUrl} alt="" loading="lazy" draggable={false} />
                  )}
                  {character.solved && (
                    <span className="object__stamp" aria-hidden="true">
                      ✓
                    </span>
                  )}
                </span>
                <span className="pairing__name">{pickLang(character.nameI18n, lang)}</span>
              </button>
            </li>
          ))}
        </ul>

        <ul className="pairing__column pairing__column--planets">
          {astres.map((planet) => {
            // Un astre déjà trouvé reste affiché mais devient inerte : le
            // retirer ferait bouger toute la colonne sous le doigt.
            const taken = state.characters.some((c) => c.solvedPlanetId === planet.id);
            return (
              <li key={planet.id}>
                <button
                  type="button"
                  className={`pairing__item pairing__item--planet ${
                    taken ? "pairing__item--solved" : ""
                  } ${missed === planet.id ? "pairing__item--miss" : ""}`}
                  disabled={chosen === null || taken}
                  onClick={() => void tryPlanet(planet.id)}
                >
                  <span className="pairing__image">
                    {planet.imageUrl !== null && (
                      <img src={planet.imageUrl} alt="" loading="lazy" draggable={false} />
                    )}
                  </span>
                  <span className="pairing__name">{pickLang(planet.nameI18n, lang)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="game__actions">
        {allSolved && <p className="game__nudge">{t("pairing.allSolved")}</p>}
        <button
          type="button"
          className={`button button--full ${allSolved ? "button--primary" : "button--ghost"}`}
          disabled={busy}
          onClick={onFinish}
        >
          {t("game.finish", { number: 2 })}
        </button>
      </div>
    </main>
  );
}
